// Pure animation math for the first-person viewmodel: springs, pose offsets and the reload / melee /
// throw / equip curves. No three.js here so it is unit-testable in node.

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Smoothstep of t clamped to 0..1. */
export function ease(t: number): number {
  const c = clamp01(t);
  return c * c * (3 - 2 * c);
}

/** Damped spring integrated with fixed substeps (stable for any frame time). */
export class Spring {
  x = 0;
  v = 0;
  constructor(
    readonly omega: number,
    readonly zeta: number,
  ) {}

  step(target: number, dt: number): void {
    const n = Math.min(Math.max(Math.ceil(dt * 120), 1), 12);
    const h = dt / n;
    const k = this.omega * this.omega;
    const c = 2 * this.zeta * this.omega;
    for (let i = 0; i < n; i++) {
      this.v += (-k * (this.x - target) - c * this.v) * h;
      this.x += this.v * h;
    }
  }

  reset(): void {
    this.x = 0;
    this.v = 0;
  }
}

/** Offset of the weapon pose relative to its base pose: metres and radians (yaw about +Y, pitch about +X, roll about +Z). */
export class PoseDelta {
  x = 0;
  y = 0;
  z = 0;
  pitch = 0;
  yaw = 0;
  roll = 0;

  reset(): this {
    this.x = this.y = this.z = this.pitch = this.yaw = this.roll = 0;
    return this;
  }

  set(x: number, y: number, z: number, pitch: number, yaw: number, roll: number): this {
    this.x = x;
    this.y = y;
    this.z = z;
    this.pitch = pitch;
    this.yaw = yaw;
    this.roll = roll;
    return this;
  }

  addScaled(p: PoseDelta, w: number): this {
    this.x += p.x * w;
    this.y += p.y * w;
    this.z += p.z * w;
    this.pitch += p.pitch * w;
    this.yaw += p.yaw * w;
    this.roll += p.roll * w;
    return this;
  }
}

/** 0 outside a reload, 1 while the gun is tilted for the magazine swap. */
export function reloadTilt(t: number): number {
  if (t < 0) return 0;
  return ease(t / 0.14) * (1 - ease((t - 0.86) / 0.14));
}

function bump(t: number, centre: number, halfWidth: number): number {
  const u = Math.abs(t - centre) / halfWidth;
  return u >= 1 ? 0 : 1 - u * u * (3 - 2 * u);
}

/**
 * Two knocks of the gun during a magazine reload: the new magazine seating (about 72 % of the
 * reload) and the charging handle (about 90 %). 0 outside them, 1 at the peak of the first.
 */
export function reloadKnock(t: number): number {
  if (t < 0) return 0;
  return Math.max(bump(t, 0.72, 0.06), 0.7 * bump(t, 0.9, 0.05));
}

/** How far the magazine has dropped out of the gun (m) and whether it is visible at reload progress t. */
export function magazineState(t: number, out: { drop: number; visible: boolean }): void {
  out.visible = true;
  out.drop = 0;
  if (t < 0.3) return;
  if (t < 0.46) {
    out.drop = 0.2 * ease((t - 0.3) / 0.16);
  } else if (t < 0.56) {
    out.drop = 0.2;
    out.visible = false;
  } else if (t < 0.72) {
    out.drop = 0.2 * (1 - ease((t - 0.56) / 0.16));
  }
}

/** Blend of the support hand moving to the magazine well (0 at the grip, 1 at the magazine). */
export function handToMagazine(t: number): number {
  if (t < 0.12 || t > 0.95) return 0;
  if (t < 0.3) return ease((t - 0.12) / 0.18);
  if (t < 0.8) return 1;
  return 1 - ease((t - 0.8) / 0.15);
}

/** Blend of the support hand carrying the old magazine away (and bringing the new one in). */
export function handCarryingMagazine(t: number): number {
  if (t < 0.3 || t > 0.72) return 0;
  if (t < 0.46) return ease((t - 0.3) / 0.16);
  if (t < 0.56) return 1;
  return 1 - ease((t - 0.56) / 0.16);
}

/** Shell-loading motion for weapons without a detachable magazine (three small dips). */
export function handShellLoading(t: number): number {
  if (t < 0.1 || t > 0.9) return 0;
  const u = (t - 0.1) / 0.8;
  return Math.abs(Math.sin(u * Math.PI * 3)) * Math.sin(u * Math.PI);
}

// x, y, z (m), pitch, yaw, roll (rad) at rest, wound up to the right, swept to the left, at rest.
const MELEE_KEYS: ReadonlyArray<readonly [number, number, number, number, number, number]> = [
  [0, 0, 0, 0, 0, 0],
  [0.09, -0.03, 0.1, 0.3, -0.5, -0.7],
  [-0.17, 0.02, -0.2, -0.3, 0.9, 0.5],
  [0, 0, 0, 0, 0, 0],
];
const MELEE_TIMES = [0, 0.3, 0.55, 1] as const;

/** Knife swing: wind-up to the right, sweep to the left, recover. `t` in (0, 1]. */
export function meleePose(t: number, out: PoseDelta): PoseDelta {
  let i = 1;
  while (i < 3 && t > MELEE_TIMES[i]) i++;
  const w = ease((t - MELEE_TIMES[i - 1]) / (MELEE_TIMES[i] - MELEE_TIMES[i - 1]));
  const a = MELEE_KEYS[i - 1];
  const b = MELEE_KEYS[i];
  return out.set(
    a[0] + (b[0] - a[0]) * w,
    a[1] + (b[1] - a[1]) * w,
    a[2] + (b[2] - a[2]) * w,
    a[3] + (b[3] - a[3]) * w,
    a[4] + (b[4] - a[4]) * w,
    a[5] + (b[5] - a[5]) * w,
  );
}

/** Throw: the weapon dips away and returns. `t` in (0, 1]. */
export function throwPose(t: number, out: PoseDelta): PoseDelta {
  const e = Math.pow(Math.sin(Math.PI * clamp01(t)), 1.2);
  return out.set(-0.05 * e, -0.2 * e, 0.06 * e, -0.45 * e, 0.2 * e, 0.3 * e);
}

/** Raising a freshly equipped weapon. `frac` 0 = lowered, 1 = ready. */
export function raisePose(frac: number, out: PoseDelta): PoseDelta {
  const k = 1 - ease(frac);
  return out.set(0, -0.3 * k, 0.06 * k, -0.6 * k, 0, 0.2 * k);
}

/** Lowering the weapon that is being put away. `frac` 0 = ready, 1 = lowered. */
export function lowerPose(frac: number, out: PoseDelta): PoseDelta {
  const k = ease(frac);
  return out.set(0, -0.3 * k, 0.04 * k, -0.7 * k, 0, -0.15 * k);
}
